// All editorial content. Each article renders through pages/ArticlePage.js.
// Content blocks: {t:"lead"|"p"|"h2"|"ul", text?, items?}

// Topic images for the blog cards.
import blogSip from "../assets/banners/blog-sip.jpg";
import blogInvest30 from "../assets/banners/blog-invest30.jpg";
import blogFdMf from "../assets/banners/blog-fdmf.jpg";
import blogTax from "../assets/banners/blog-tax.jpg";

export const ARTICLES = [
  /* ------------------ PERSONAL FINANCE ------------------ */
  {
    slug: "budgeting-guide",
    category: "personal-finance",
    title: "The Complete Budgeting Guide for Beginners",
    emoji: "📒",
    readTime: 11,
    excerpt: "Learn the 50/30/20 rule and build a budget that actually sticks.",
    content: [
      { t: "lead", text: "A budget is simply a plan for your money. Done right, it tells every rupee where to go before the month begins, so you spend with intent instead of wondering where your salary disappeared. This guide takes you from zero to a working budget you can actually stick to." },
      { t: "h2", text: "What a budget really is (and isn't)" },
      { t: "p", text: "A budget is not about restriction or guilt. It is a tool for awareness and control. It does not stop you from spending on the things you enjoy; it simply makes sure those things are planned and affordable. Think of it as giving every rupee a job before it slips through your fingers." },
      { t: "p", text: "Most people assume they overspend on big purchases. In reality, financial leaks are almost always small and repeated: daily food delivery, impulse online orders, forgotten subscriptions and 'just ₹200' purchases that quietly add up to thousands every month. A budget makes these invisible leaks visible." },
      { t: "h2", text: "Why budgeting matters more than your income" },
      { t: "p", text: "Two people earning the same salary can have completely different financial lives. One feels broke every month; the other steadily builds wealth. The difference is rarely income, it is the system. Budgeting is that system. It turns 'I hope I have money left' into 'I know exactly what I can spend'." },
      { t: "ul", items: [
        "It reduces money stress because there are no nasty surprises at month-end.",
        "It frees up cash you didn't know you had for savings and investing.",
        "It helps you reach goals faster, whether that is a vacation, a car or financial freedom.",
        "It protects you from debt by keeping spending below income.",
      ] },
      { t: "h2", text: "The 50/30/20 rule explained" },
      { t: "p", text: "The simplest budgeting framework splits your after-tax income into three buckets. It is flexible, easy to remember and perfect for beginners." },
      { t: "ul", items: [
        "50% Needs: rent, EMIs, groceries, utilities, transport, insurance, basic essentials.",
        "30% Wants: dining out, subscriptions, shopping, travel, hobbies and entertainment.",
        "20% Savings and investments: SIPs, emergency fund, retirement and extra debt repayment.",
      ] },
      { t: "p", text: "Example: if you take home ₹50,000 a month, that is ₹25,000 for needs, ₹15,000 for wants and ₹10,000 towards savings and investments. If your needs exceed 50%, that is a signal to either increase income or reduce fixed costs, not to skip saving." },
      { t: "h2", text: "Other budgeting methods to consider" },
      { t: "ul", items: [
        "Zero-based budget: assign every rupee a purpose until income minus allocations equals zero. Great for detail-lovers.",
        "Envelope system: set fixed cash or digital limits per category and stop spending once an envelope is empty.",
        "Pay-yourself-first: automate savings the moment salary arrives, then freely spend what remains.",
      ] },
      { t: "h2", text: "How to build your budget in 5 steps" },
      { t: "ul", items: [
        "Step 1: Calculate your real monthly take-home income, including any side earnings.",
        "Step 2: Track every expense for one full month using an app or a simple spreadsheet.",
        "Step 3: Sort spending into needs, wants and savings to see where money actually goes.",
        "Step 4: Set realistic limits for each category, slightly tighter than last month.",
        "Step 5: Automate savings on salary day so you pay yourself before you can spend.",
      ] },
      { t: "h2", text: "Common budgeting mistakes to avoid" },
      { t: "ul", items: [
        "Being too strict, then abandoning the budget after one bad week.",
        "Forgetting irregular expenses like insurance premiums, festivals and repairs.",
        "Not keeping a small 'fun' allowance, which makes the budget impossible to sustain.",
        "Tracking once and never reviewing, budgets need a monthly check-in.",
      ] },
      { t: "h2", text: "Pro tip: make saving automatic" },
      { t: "p", text: "Treat savings as a fixed expense, not whatever is left over at the end of the month, because there is rarely anything left over. Set up an automatic SIP or transfer on the 1st of every month. This single habit, automating the 'pay yourself first' rule, is the highest-impact money move most people can make." },
    ],
  },
  {
    slug: "money-saving-tips",
    category: "personal-finance",
    title: "25 Practical Money Saving Tips That Actually Work",
    emoji: "💵",
    readTime: 10,
    excerpt: "Small, repeatable habits that quietly add up to a big corpus.",
    content: [
      { t: "lead", text: "Saving money isn't about extreme frugality or never enjoying life. It is about cutting waste without cutting joy, so the money you save can quietly grow into real wealth. Here are proven, practical habits you can start today." },
      { t: "h2", text: "Why small savings matter so much" },
      { t: "p", text: "Saving ₹200 a day sounds trivial, but that is ₹6,000 a month and ₹72,000 a year. Invested in an equity fund at 12%, that single habit can grow to over ₹13 lakh in 10 years. Small leaks sink big ships, and small savings build big fortunes." },
      { t: "h2", text: "Everyday spending habits" },
      { t: "ul", items: [
        "Wait 48 hours before any non-essential purchase above ₹2,000 to beat impulse buying.",
        "Cook at home 5 days a week; eating out and food delivery is the number one budget killer.",
        "Carry a water bottle and plan meals to avoid daily small spends that add up fast.",
        "Make a shopping list before you go and stick to it, never shop hungry or bored.",
        "Use cash or UPI limits for discretionary spending so you physically feel the outflow.",
      ] },
      { t: "h2", text: "Cut recurring and hidden costs" },
      { t: "ul", items: [
        "Audit subscriptions every quarter and cancel anything you haven't used in a month.",
        "Review your phone, internet and OTT plans; you are often paying for more than you need.",
        "Switch to annual plans only for services you are certain to use long-term.",
        "Scan UPI and credit-card statements to catch recurring 'invisible' charges.",
        "Negotiate or compare insurance premiums at renewal instead of auto-renewing.",
      ] },
      { t: "h2", text: "Smarter money moves" },
      { t: "ul", items: [
        "Move idle savings-account cash into a liquid fund or sweep-in FD for better returns.",
        "Build an emergency fund so you never have to borrow at high interest during a crisis.",
        "Increase your SIP by 10% every time your salary rises, before lifestyle creep kicks in.",
        "Use cashback and reward cards responsibly, but only on spends you would make anyway.",
        "Buy quality for items you use daily; cheap things that break cost more over time.",
      ] },
      { t: "h2", text: "Beat lifestyle inflation" },
      { t: "p", text: "The biggest threat to saving is lifestyle inflation, the tendency to spend more as you earn more. When you get a raise, commit to saving at least half of the increase before upgrading your lifestyle. This way your wealth grows alongside your income instead of your expenses." },
      { t: "h2", text: "The mindset that makes it stick" },
      { t: "p", text: "The goal isn't to save a fortune overnight or to feel deprived. It is to make saving automatic and painless, so good financial behaviour happens by default. Focus on building systems, not relying on willpower, and the savings will take care of themselves." },
    ],
  },
  {
    slug: "emergency-fund-guide",
    category: "personal-finance",
    title: "Emergency Fund Guide: Your Financial Safety Net",
    emoji: "🛡️",
    readTime: 9,
    excerpt: "How much to save, where to keep it, and why it comes before investing.",
    content: [
      { t: "lead", text: "An emergency fund is money set aside for genuine, unexpected emergencies: a job loss, a medical bill, an urgent home or vehicle repair. It is the foundation that every other financial plan stands on, and the first thing you should build before investing." },
      { t: "h2", text: "Why you need one before investing" },
      { t: "p", text: "Life is unpredictable. Without a safety net, a single emergency forces you to either sell investments at the worst possible time or borrow at painful interest rates. An emergency fund breaks this cycle. It lets your long-term investments stay invested and keeps you out of high-cost debt when life happens." },
      { t: "p", text: "Think of it as insurance you pay to yourself. The peace of mind alone, knowing you can handle a shock without panic, is worth the effort of building it." },
      { t: "h2", text: "How much do you need?" },
      { t: "p", text: "The standard guideline is 3 to 6 months of essential expenses, not your full income. Calculate only the non-negotiables: rent, EMIs, groceries, utilities, insurance and transport." },
      { t: "ul", items: [
        "3 months: if you have a very stable job and dual household income.",
        "6 months: for most salaried individuals as a comfortable baseline.",
        "9 to 12 months: if you are self-employed, freelance, have irregular income or are the sole earner.",
      ] },
      { t: "p", text: "Example: if your essential monthly expenses are ₹40,000, aim for an emergency fund of ₹1.2 lakh to ₹2.4 lakh depending on your situation." },
      { t: "h2", text: "Where to keep your emergency fund" },
      { t: "p", text: "The two rules are safety and quick access. This money should never be exposed to market risk; it must be there the moment you need it." },
      { t: "ul", items: [
        "A separate high-interest savings account for instant, anytime access.",
        "A liquid mutual fund for slightly better returns with typically one-day liquidity.",
        "A sweep-in fixed deposit that earns FD-level rates but can be broken penalty-free.",
      ] },
      { t: "h2", text: "How to build it from scratch" },
      { t: "ul", items: [
        "Set a clear target amount based on your essential expenses.",
        "Automate a fixed monthly transfer until you hit the goal, even if it is small.",
        "Redirect bonuses, tax refunds and windfalls straight into the fund to speed it up.",
        "Once funded, stop adding and channel that money into investments instead.",
      ] },
      { t: "h2", text: "Rules of the fund" },
      { t: "ul", items: [
        "Keep it completely separate from your everyday account so you are not tempted to dip in.",
        "Only touch it for true emergencies, not sales, gadgets, weddings or vacations.",
        "Refill it as soon as possible after you use it, treating replenishment as a priority.",
        "Review the target yearly, as your expenses and responsibilities grow over time.",
      ] },
    ],
  },
  {
    slug: "credit-score-explained",
    category: "personal-finance",
    title: "Credit Score Explained: How to Build and Protect It",
    emoji: "📊",
    readTime: 10,
    excerpt: "What a CIBIL score is, what affects it, and how to push it above 750.",
    content: [
      { t: "lead", text: "Your credit score is a 3-digit number, usually between 300 and 900, that tells lenders how reliably you repay borrowed money. A score above 750 unlocks faster approvals, higher limits and the lowest interest rates on loans and credit cards." },
      { t: "h2", text: "What is a credit score?" },
      { t: "p", text: "In India, credit scores are maintained by bureaus like CIBIL, Experian, Equifax and CRIF High Mark. Every time you take a loan or use a credit card, your repayment behaviour is reported to these bureaus, who turn your history into a single score. Lenders use it to decide whether to lend to you and at what rate." },
      { t: "p", text: "A higher score signals lower risk to the lender, which translates directly into cheaper borrowing for you. Over a home loan's lifetime, a good score versus a poor one can mean a difference of several lakh rupees in interest." },
      { t: "h2", text: "What affects your score" },
      { t: "ul", items: [
        "Payment history (around 35%): paying every EMI and card bill on time is the single biggest factor.",
        "Credit utilisation (around 30%): keep your card spending below 30% of your total limit.",
        "Age of credit (around 15%): older accounts help your score, so don't close your oldest card.",
        "Credit mix: a healthy blend of secured (home, auto) and unsecured (card, personal) loans helps.",
        "New enquiries: applying for many loans or cards in a short span lowers your score.",
      ] },
      { t: "h2", text: "How to build and improve it" },
      { t: "ul", items: [
        "Set auto-pay or reminders so you never miss a due date, even by a day.",
        "Pay the full statement balance every month, not just the minimum due.",
        "Keep utilisation low; request a higher credit limit to improve your ratio without spending more.",
        "Avoid applying for multiple credit products at once; space out applications.",
        "Keep old, well-managed accounts open to lengthen your credit history.",
        "Check your credit report at least once a year and dispute any errors you find.",
      ] },
      { t: "h2", text: "How long does it take to improve?" },
      { t: "p", text: "Credit scores move slowly because they reward consistency. Expect to see meaningful improvement over 6 to 12 months of disciplined behaviour. There are no genuine shortcuts; anyone promising to instantly fix your score for a fee should be treated with suspicion." },
      { t: "h2", text: "Myths to ignore" },
      { t: "ul", items: [
        "Checking your own score does not hurt it; that is a 'soft' enquiry.",
        "You do not need to carry a balance or pay interest to build credit; paying in full is best.",
        "Closing old cards does not help; it often shortens your history and raises utilisation.",
      ] },
    ],
  },
  {
    slug: "debt-management",
    category: "personal-finance",
    title: "Debt Management: How to Become Debt-Free Faster",
    emoji: "⛓️",
    readTime: 10,
    excerpt: "Snowball vs avalanche, and a step-by-step plan to clear what you owe.",
    content: [
      { t: "lead", text: "Not all debt is bad. A home loan can build an asset, and an education loan can build a career. But high-interest debt like credit cards and personal loans can quietly drain your wealth. This guide shows you how to take control and get out of it fast." },
      { t: "h2", text: "Good debt vs bad debt" },
      { t: "p", text: "Good debt is borrowing that helps you build long-term value at a reasonable rate, such as a home loan or an education loan. Bad debt is high-interest borrowing for things that lose value or for consumption, such as credit-card balances, payday loans or EMIs on luxury purchases. Your first priority is always to eliminate bad, high-interest debt." },
      { t: "h2", text: "Know your numbers first" },
      { t: "p", text: "You cannot fix what you cannot see. Before choosing a strategy, list every single debt you have. This one exercise often reveals just how much interest is silently eating your income." },
      { t: "ul", items: [
        "The outstanding balance on each loan or card.",
        "The interest rate (APR) for each one.",
        "The minimum monthly payment required.",
        "The due date, so you never miss a payment.",
      ] },
      { t: "h2", text: "Two proven payoff methods" },
      { t: "ul", items: [
        "Avalanche method: pay off the highest interest-rate debt first while paying minimums on the rest. This saves you the most money mathematically.",
        "Snowball method: pay off the smallest balance first to get quick wins. This builds momentum and motivation, which keeps many people going.",
      ] },
      { t: "p", text: "The avalanche saves more money; the snowball builds more motivation. Pick the one you will actually stick with, because consistency beats theory." },
      { t: "h2", text: "A step-by-step plan to become debt-free" },
      { t: "ul", items: [
        "Always pay at least the minimum on every debt to protect your credit score.",
        "Pick one target debt (highest rate or smallest balance) and throw every extra rupee at it.",
        "Once a debt is cleared, roll its payment into the next target, accelerating each payoff.",
        "Consider a balance transfer or consolidation loan to reduce the interest rate.",
        "Pause non-essential spending temporarily to free up cash for faster repayment.",
        "Once debt-free, redirect those freed-up EMIs straight into savings and investments.",
      ] },
      { t: "h2", text: "How to avoid falling back into debt" },
      { t: "ul", items: [
        "Build an emergency fund so the next surprise doesn't go on a credit card.",
        "Pay credit-card bills in full every month, never just the minimum.",
        "Stick to a budget so spending stays below income.",
        "Avoid taking new EMIs for wants you could save up for instead.",
      ] },
    ],
  },
  {
    slug: "financial-planning-beginners",
    category: "personal-finance",
    title: "Financial Planning for Beginners: A Roadmap",
    emoji: "🗺️",
    readTime: 11,
    excerpt: "The exact order to fix your finances, from safety net to wealth.",
    content: [
      { t: "lead", text: "Financial planning sounds intimidating, but it is really just a sequence of sensible steps done in the right order. Skip steps and you build on sand; follow them and your money works harder every year. This roadmap shows you exactly where to start and what comes next." },
      { t: "h2", text: "What financial planning actually means" },
      { t: "p", text: "Financial planning is simply aligning your money with your life goals. It answers three questions: where are you now, where do you want to be, and what steps bridge the gap? You don't need to be wealthy or a finance expert to start; you just need to begin in the right order." },
      { t: "h2", text: "The order of priorities" },
      { t: "p", text: "This sequence matters. Each step builds the stability needed for the next. Don't jump to step 5 before you've done steps 1 to 4." },
      { t: "ul", items: [
        "1. Budget: know exactly where your money goes each month.",
        "2. Emergency fund: save 3 to 6 months of essential expenses for safety.",
        "3. Insurance: get adequate term life and health cover to protect your family.",
        "4. Clear high-interest debt: eliminate credit cards and personal loans first.",
        "5. Invest for goals: start SIPs in equity for long-term wealth creation.",
        "6. Retirement and tax planning: use PPF, NPS and ELSS to grow and save tax.",
      ] },
      { t: "h2", text: "Why insurance comes before investing" },
      { t: "p", text: "Many beginners rush to invest while ignoring insurance. That is risky. A single medical emergency or the loss of an earning member can wipe out years of investments. Buy a pure term life plan (cover of roughly 10 to 15 times your annual income) and a solid health insurance policy first. Keep insurance and investment separate; avoid mixing them in costly endowment plans." },
      { t: "h2", text: "Set SMART goals" },
      { t: "p", text: "Vague goals like 'save more' rarely work. Every financial goal should be Specific, Measurable, Achievable, Relevant and Time-bound. For example: 'Build a ₹5 lakh emergency fund in 18 months' or 'Invest ₹10,000 a month for my child's education over 15 years'." },
      { t: "ul", items: [
        "Short-term goals (under 3 years): keep money safe in FDs or debt funds.",
        "Medium-term goals (3 to 7 years): use a balanced mix of equity and debt.",
        "Long-term goals (7+ years): lean into equity, where time smooths out volatility.",
      ] },
      { t: "h2", text: "Review and adjust regularly" },
      { t: "p", text: "A financial plan is not 'set and forget'. Review it at least once a year and whenever a major life event happens, such as marriage, a child, a new job or a big raise. Rebalance your investments, increase your SIPs as income grows, and update your insurance cover. Small, consistent adjustments keep your plan on track for decades." },
    ],
  },

  /* ------------------ INVESTMENT ------------------ */
  {
    slug: "mutual-funds-guide",
    category: "investment",
    title: "Mutual Funds Guide: Everything You Need to Know",
    emoji: "📈",
    readTime: 12,
    excerpt: "Types, costs, taxation and how to pick the right fund for your goal.",
    content: [
      { t: "lead", text: "A mutual fund pools money from many investors and a professional fund manager invests it in stocks, bonds or both. You get instant diversification, expert management and the ability to start with as little as ₹100, all at a low cost. This guide covers everything a beginner needs to invest with confidence." },
      { t: "h2", text: "How mutual funds work" },
      { t: "p", text: "When you invest in a mutual fund, your money is combined with thousands of other investors. The fund manager uses this pool to buy a basket of securities according to the fund's objective. You are allotted 'units' based on your investment, and the value of each unit is the NAV (Net Asset Value), which updates daily based on the underlying holdings." },
      { t: "p", text: "Your returns come in two ways: appreciation in the NAV as the holdings grow in value, and any dividends the fund distributes. Because the fund holds many securities at once, the poor performance of any single stock has a limited impact on your overall investment." },
      { t: "h2", text: "Main types of mutual funds" },
      { t: "ul", items: [
        "Equity funds: invest mainly in stocks; higher risk but the highest long-term return potential.",
        "Debt funds: invest in bonds and fixed-income securities; lower risk with steady, modest returns.",
        "Hybrid funds: blend equity and debt to balance growth and stability.",
        "Index funds: passively track an index like the Nifty 50 at a very low cost.",
        "ELSS funds: equity funds that also offer tax savings under Section 80C with a 3-year lock-in.",
        "Liquid funds: ultra-low-risk debt funds ideal for parking an emergency fund.",
      ] },
      { t: "h2", text: "Equity fund sub-categories" },
      { t: "ul", items: [
        "Large-cap: invest in the top 100 established companies; relatively stable.",
        "Mid-cap and small-cap: higher growth potential but more volatile.",
        "Flexi-cap: invest across company sizes, giving the manager flexibility.",
        "Sectoral/thematic: focus on one sector like IT or banking; higher concentration risk.",
      ] },
      { t: "h2", text: "What to check before investing" },
      { t: "ul", items: [
        "Expense ratio: the annual fee charged; lower is better as it directly reduces returns.",
        "Fund category and risk level: match it to your goal and time horizon.",
        "Long-term track record: look at performance across multiple market cycles, not just last year.",
        "Direct vs regular plans: always prefer direct plans to avoid commission drag.",
        "Fund manager experience and the fund house's reputation.",
        "Assets under management (AUM) and how consistent the strategy has been.",
      ] },
      { t: "h2", text: "SIP vs lump sum" },
      { t: "p", text: "A SIP (Systematic Investment Plan) invests a fixed amount every month, averaging your purchase cost over time and removing the need to time the market. A lump sum invests a large amount at once, which can work well when markets are low or you have a windfall. For most salaried investors, a disciplined monthly SIP is the simplest and most effective approach." },
      { t: "h2", text: "Taxation in India" },
      { t: "p", text: "Equity funds held over 1 year attract 12.5% long-term capital gains tax on gains above ₹1.25 lakh per year; sold within a year, gains are taxed at 20%. Debt funds are taxed at your income slab regardless of holding period. Always factor tax into your real, take-home returns." },
      { t: "h2", text: "Common mistakes to avoid" },
      { t: "ul", items: [
        "Chasing last year's top performer instead of a consistent long-term fund.",
        "Stopping SIPs in a market fall, exactly when you should keep buying.",
        "Holding too many overlapping funds; 3 to 5 well-chosen funds are usually enough.",
        "Ignoring the expense ratio, which quietly compounds against you over decades.",
      ] },
    ],
  },
  {
    slug: "stock-market-basics",
    category: "investment",
    title: "Stock Market Basics: Start Investing With Confidence",
    emoji: "🏛️",
    readTime: 11,
    excerpt: "Shares, exchanges, demat accounts and how to avoid rookie mistakes.",
    content: [
      { t: "lead", text: "A stock represents part-ownership in a company. When the company grows and profits, so does the value of your shares; that is how long-term investors build serious wealth. This guide explains how the market works and how to start investing without the fear." },
      { t: "h2", text: "What is a stock, really?" },
      { t: "p", text: "When you buy a share of a company, you own a tiny piece of that business. If the company performs well, your share becomes more valuable, and you may also receive dividends, a portion of profits paid to shareholders. Over the long run, owning shares of good businesses has historically been one of the best ways to beat inflation and grow money." },
      { t: "h2", text: "How the stock market works" },
      { t: "p", text: "Companies list their shares on stock exchanges through an IPO (Initial Public Offering). After that, investors buy and sell those shares among themselves on the exchange. Prices move based on demand and supply, company performance, and overall economic sentiment." },
      { t: "h2", text: "Key concepts every beginner should know" },
      { t: "ul", items: [
        "Exchanges: the NSE and BSE are where shares are bought and sold in India.",
        "Demat and trading account: required to hold shares electronically and to trade them.",
        "Indices: the Nifty 50 and Sensex track the overall direction of the market.",
        "Dividends: a portion of company profits paid out to shareholders.",
        "Market capitalisation: the total value of a company, classed as large, mid or small cap.",
        "Bull and bear markets: rising and falling market phases.",
      ] },
      { t: "h2", text: "How to start investing in stocks" },
      { t: "ul", items: [
        "Step 1: Complete your KYC and open a demat and trading account with a broker.",
        "Step 2: Start small and invest only money you won't need for at least 5 years.",
        "Step 3: Research a company's business, financials and valuation before buying.",
        "Step 4: Diversify across sectors instead of betting on one or two stocks.",
        "Step 5: Stay invested through ups and downs; wealth is built over years, not days.",
      ] },
      { t: "h2", text: "Beginner mistakes to avoid" },
      { t: "ul", items: [
        "Chasing tips and 'hot' stocks from social media without doing your own research.",
        "Trying to time the market instead of staying consistently invested.",
        "Putting all your money into one or two stocks.",
        "Panic-selling during normal market corrections, locking in losses.",
        "Using borrowed money or leverage you can't afford to lose.",
      ] },
      { t: "h2", text: "The easiest way to start" },
      { t: "p", text: "If analysing individual companies feels overwhelming, you don't have to. Start with a low-cost index fund or ETF that tracks the Nifty 50; it gives you instant diversification across India's top companies. As you learn more, you can gradually explore individual stocks with a small portion of your portfolio." },
    ],
  },
  {
    slug: "etf-vs-mutual-fund",
    category: "investment",
    title: "ETF vs Mutual Fund: Which Should You Choose?",
    emoji: "⚖️",
    readTime: 9,
    excerpt: "The real differences in cost, liquidity and convenience.",
    content: [
      { t: "lead", text: "ETFs and mutual funds both give you a diversified basket of securities in a single investment, but they trade, cost and behave differently. Understanding these differences helps you pick the right vehicle for your style." },
      { t: "h2", text: "What is an ETF?" },
      { t: "p", text: "An Exchange-Traded Fund (ETF) is a basket of securities, often tracking an index, that trades on the stock exchange just like a single share. Its price changes throughout the trading day as people buy and sell it. You need a demat and trading account to invest in ETFs." },
      { t: "h2", text: "What is a mutual fund?" },
      { t: "p", text: "A mutual fund also pools money into a diversified basket, but it is bought and sold directly with the fund house at one price per day, the NAV. You don't need a demat account, and you can easily automate monthly SIPs." },
      { t: "h2", text: "Key differences side by side" },
      { t: "ul", items: [
        "Trading: ETFs trade live on the exchange; mutual funds are priced once a day at NAV.",
        "Cost: ETFs usually have lower expense ratios, but you pay brokerage and need a demat account.",
        "SIP convenience: mutual funds make automated monthly SIPs effortless; ETF SIPs are clumsier.",
        "Minimum investment: ETFs can be bought as a single unit; funds often start at ₹100 to ₹500.",
        "Liquidity: ETFs depend on market trading volume; mutual funds are always transacted at NAV.",
      ] },
      { t: "h2", text: "Pros and cons" },
      { t: "ul", items: [
        "ETF pros: lower cost, intraday flexibility, full transparency of holdings.",
        "ETF cons: needs a demat account, brokerage on every trade, pricing can deviate from NAV.",
        "Mutual fund pros: effortless SIPs, no demat needed, professional management.",
        "Mutual fund cons: slightly higher expense ratio, only one price point per day.",
      ] },
      { t: "h2", text: "Which is right for you?" },
      { t: "p", text: "If you prefer hands-off, automated investing through monthly SIPs, index mutual funds are the simpler choice. If you want the lowest cost, already have a demat account and are comfortable placing trades, ETFs may suit you better. Many investors successfully use both." },
    ],
  },
  {
    slug: "gold-investment",
    category: "investment",
    title: "Gold Investment in India: Physical, Digital or SGB?",
    emoji: "🥇",
    readTime: 10,
    excerpt: "Compare physical gold, gold ETFs, digital gold and Sovereign Gold Bonds.",
    content: [
      { t: "lead", text: "Gold has been a store of value for centuries and remains a classic hedge against inflation and economic uncertainty. But how you buy it makes a big difference to your returns, costs and safety. Here's a clear comparison of every option available in India." },
      { t: "h2", text: "Why invest in gold?" },
      { t: "p", text: "Gold tends to hold its value and often rises when stock markets fall or inflation spikes, which makes it a useful diversifier. It is not meant to be a primary wealth-builder like equity; instead, it acts as insurance for your portfolio. Most advisers suggest keeping gold to around 5 to 10% of your total investments." },
      { t: "h2", text: "Ways to own gold" },
      { t: "ul", items: [
        "Physical gold (jewellery, coins, bars): tangible, but involves making charges, storage cost and purity risk.",
        "Gold ETFs: track the gold price, held in your demat account, easy to buy and sell.",
        "Gold mutual funds: invest in gold ETFs; allow SIPs and need no demat account.",
        "Digital gold: buy small amounts online, backed by physical gold stored by the provider.",
        "Sovereign Gold Bonds (SGB): government bonds that pay around 2.5% annual interest plus gold price gains.",
      ] },
      { t: "h2", text: "Comparing the options" },
      { t: "ul", items: [
        "Returns: SGBs add interest on top of price gains; physical gold loses value to making charges.",
        "Safety: SGBs and ETFs carry no theft or storage risk, unlike physical gold.",
        "Liquidity: ETFs and digital gold are easy to sell; SGBs have an 8-year tenure (exit allowed after 5).",
        "Tax: SGB gains are tax-free if held to maturity, a major advantage.",
      ] },
      { t: "h2", text: "The smart pick" },
      { t: "p", text: "For pure long-term investment, Sovereign Gold Bonds are hard to beat: you earn 2.5% interest on top of any gold price appreciation, with no storage worry and tax-free gains at maturity. If you want flexibility and easy trading, gold ETFs are a strong choice. Buy physical gold mainly for personal or cultural use, not as an investment." },
    ],
  },
  {
    slug: "ppf-nps-fd-comparison",
    category: "investment",
    title: "PPF vs NPS vs FD: Which Is Best for You?",
    emoji: "🏦",
    readTime: 11,
    excerpt: "Returns, lock-in, tax benefits and liquidity compared side by side.",
    content: [
      { t: "lead", text: "PPF, NPS and Fixed Deposits are three of India's most popular safe investments, but they serve very different goals. Choosing the right one depends on your time horizon, tax bracket and need for flexibility." },
      { t: "h2", text: "PPF (Public Provident Fund)" },
      { t: "p", text: "PPF is a government-backed savings scheme offering around 7.1% tax-free returns with a 15-year lock-in. Contributions qualify for Section 80C deduction, the interest is tax-free, and the maturity amount is tax-free too, the rare EEE (Exempt-Exempt-Exempt) status. It is ideal for safe, long-term goals like a child's education." },
      { t: "ul", items: [
        "Returns: around 7.1%, fully tax-free.",
        "Lock-in: 15 years (partial withdrawal allowed from year 7).",
        "Risk: virtually zero, backed by the government.",
        "Best for: safe, long-term, tax-free goals.",
      ] },
      { t: "h2", text: "NPS (National Pension System)" },
      { t: "p", text: "NPS is a market-linked retirement product where your money is invested in a mix of equity and debt. It offers an extra ₹50,000 tax deduction under Section 80CCD(1B), over and above the 80C limit. The catch is that it is locked until age 60, and a portion must be used to buy an annuity at maturity." },
      { t: "ul", items: [
        "Returns: market-linked, typically 9 to 12% over the long term.",
        "Lock-in: until age 60.",
        "Tax: extra ₹50,000 deduction under 80CCD(1B).",
        "Best for: disciplined retirement saving with bonus tax benefits.",
      ] },
      { t: "h2", text: "FD (Fixed Deposit)" },
      { t: "p", text: "An FD offers a fixed, guaranteed return for a chosen tenure. It is the most flexible of the three, you can pick any duration and break it early (with a small penalty). However, the interest is fully taxable at your income slab, which lowers real returns for higher earners." },
      { t: "ul", items: [
        "Returns: fixed, around 6 to 7.5%, fully taxable.",
        "Lock-in: flexible (7 days to 10 years).",
        "Risk: very low; insured up to ₹5 lakh per bank.",
        "Best for: short-term goals and capital safety.",
      ] },
      { t: "h2", text: "How to choose" },
      { t: "ul", items: [
        "Want guaranteed tax-free returns for a 15-year goal? Choose PPF.",
        "Building a retirement corpus and want extra tax savings? Choose NPS.",
        "Need safety with flexibility for short-term goals? Choose an FD.",
        "Best approach: many investors use a mix, PPF and NPS for the long term, FDs for short-term needs.",
      ] },
    ],
  },
  {
    slug: "retirement-planning",
    category: "investment",
    title: "Retirement Planning: How to Build a Lasting Corpus",
    emoji: "🌴",
    readTime: 11,
    excerpt: "Why starting early matters more than how much you invest.",
    content: [
      { t: "lead", text: "Retirement may feel decades away, but it is the one goal where starting early matters more than anything else. Compounding rewards those who begin in their 20s far more than those who invest larger amounts later. This guide shows you how to build a corpus that lasts." },
      { t: "h2", text: "The power of starting early" },
      { t: "p", text: "Consider two people. Riya invests ₹10,000 a month from age 25 to 60. Arjun invests ₹20,000 a month but only starts at 35. Despite investing twice as much each month, Arjun often ends up with a smaller corpus, because Riya's money had an extra ten years to compound. Time in the market is your single biggest advantage." },
      { t: "h2", text: "How much will you need?" },
      { t: "p", text: "A common rule of thumb is the 25x rule: aim for a corpus of about 25 times your expected annual expenses at retirement. At a safe 4% withdrawal rate, this can fund your lifestyle indefinitely. Remember to account for inflation; what costs ₹40,000 a month today could cost far more in 30 years." },
      { t: "h2", text: "Build your retirement stack" },
      { t: "ul", items: [
        "Equity mutual fund SIPs: the long-term growth engine of your retirement plan.",
        "NPS: disciplined, tax-efficient retirement saving with an extra ₹50,000 deduction.",
        "PPF and EPF: the safe, guaranteed portion of your corpus.",
        "Gradually shift from equity to debt as you approach retirement to protect your savings.",
      ] },
      { t: "h2", text: "A simple age-based approach" },
      { t: "ul", items: [
        "In your 20s and 30s: invest aggressively, mostly in equity, and start your SIPs now.",
        "In your 40s: increase contributions as income peaks; keep a healthy equity allocation.",
        "In your 50s: begin moving a portion into debt and safer instruments to reduce risk.",
        "Near retirement: hold 2 to 3 years of expenses in safe assets to avoid selling equity in a downturn.",
      ] },
      { t: "h2", text: "Avoid these retirement traps" },
      { t: "ul", items: [
        "Delaying the start, the most expensive mistake of all.",
        "Withdrawing your EPF when changing jobs instead of letting it compound.",
        "Underestimating inflation and medical costs in old age.",
        "Relying on a single source, like only EPF, instead of a diversified mix.",
      ] },
      { t: "p", text: "Use our Retirement Calculator to find the exact monthly investment your retirement goal needs." },
    ],
  },

  /* ------------------ FINANCE EDUCATION ------------------ */
  {
    slug: "stock-market-a-to-z",
    category: "education",
    title: "Stock Market A to Z: The Complete Beginner's Glossary",
    emoji: "🔤",
    readTime: 12,
    excerpt: "Every key term explained in plain language, from IPO to P/E ratio.",
    content: [
      { t: "lead", text: "The stock market has its own language, and that jargon is what makes it feel intimidating. Master these terms and the financial news, your investment app and expert commentary will finally make sense. Here is a plain-language glossary every beginner should know." },
      { t: "h2", text: "Market basics" },
      { t: "ul", items: [
        "Share/Stock: a unit of ownership in a company.",
        "Stock exchange: the marketplace (NSE, BSE) where shares are traded.",
        "Bull market: a period of rising prices and optimism.",
        "Bear market: a period of falling prices and pessimism.",
        "IPO: an Initial Public Offering, when a company first sells shares to the public.",
        "Broker: the platform or firm through which you buy and sell shares.",
      ] },
      { t: "h2", text: "Valuation and company terms" },
      { t: "ul", items: [
        "Market cap: the total value of a company's shares (large-cap, mid-cap, small-cap).",
        "P/E ratio: price relative to earnings; a common gauge of whether a stock is cheap or expensive.",
        "EPS: Earnings Per Share, a company's profit divided by its number of shares.",
        "Dividend: a portion of profit paid out to shareholders.",
        "Book value: a company's net worth as per its balance sheet.",
      ] },
      { t: "h2", text: "Portfolio and risk terms" },
      { t: "ul", items: [
        "Portfolio: your overall collection of investments.",
        "Diversification: spreading money across assets to reduce risk.",
        "Volatility: how sharply prices move up and down.",
        "Liquidity: how easily an investment can be converted to cash.",
        "Asset allocation: how you split money between equity, debt and other assets.",
      ] },
      { t: "h2", text: "Index and fund terms" },
      { t: "ul", items: [
        "Index: a benchmark like the Nifty 50 or Sensex that tracks the market.",
        "Index fund/ETF: a fund that simply mirrors an index at low cost.",
        "NAV: Net Asset Value, the per-unit price of a mutual fund.",
        "SIP: a Systematic Investment Plan, investing a fixed amount regularly.",
      ] },
      { t: "h2", text: "Where beginners should start" },
      { t: "p", text: "Don't try to memorise everything at once. Focus first on market cap, the P/E ratio and diversification; these three concepts will guide most of your early decisions. The rest will become familiar naturally as you start investing and following the market." },
    ],
  },
  {
    slug: "what-is-mutual-fund",
    category: "education",
    title: "What Is a Mutual Fund? A Simple Explanation",
    emoji: "💡",
    readTime: 8,
    excerpt: "The simplest way to understand mutual funds, with a real-world analogy.",
    content: [
      { t: "lead", text: "Imagine a group of friends pooling money to hire an expert chef. Each person pays a little, and everyone shares the feast. A mutual fund works exactly the same way, but for investing. This is the simplest way to understand one of the most powerful wealth-building tools available." },
      { t: "h2", text: "The simple analogy" },
      { t: "p", text: "On your own, you might not have enough money to buy shares in 50 different companies, nor the time and expertise to research them. A mutual fund solves this. Thousands of investors pool their money, and a professional manager invests it in a diversified basket on everyone's behalf. You share in the gains (and risks) in proportion to what you invested." },
      { t: "h2", text: "How it works step by step" },
      { t: "ul", items: [
        "Many investors pool their money into a single fund.",
        "A professional fund manager invests it in stocks, bonds or both.",
        "Each investor owns 'units' proportional to the amount they invested.",
        "The value of each unit is the NAV (Net Asset Value), updated at the end of every trading day.",
        "As the holdings grow in value, so does your NAV and your investment.",
      ] },
      { t: "h2", text: "Why people love mutual funds" },
      { t: "ul", items: [
        "Instant diversification: your money is spread across many securities, reducing risk.",
        "Professional management: experts handle the research and decisions.",
        "Affordable: you can start with as little as ₹100 a month through a SIP.",
        "Convenient: fully online, easy to start, pause or redeem.",
        "Regulated: overseen by SEBI, offering investor protection and transparency.",
      ] },
      { t: "h2", text: "Are there any downsides?" },
      { t: "p", text: "Mutual funds charge a small annual fee called the expense ratio, and returns are not guaranteed since they depend on the market. But for most people, the benefits of low-cost diversification and expert management far outweigh these. Choosing low-cost direct plans keeps fees to a minimum." },
    ],
  },
  {
    slug: "what-is-sip",
    category: "education",
    title: "What Is SIP? Systematic Investing Made Simple",
    emoji: "🔁",
    readTime: 9,
    excerpt: "How investing a fixed amount monthly beats trying to time the market.",
    content: [
      { t: "lead", text: "A SIP (Systematic Investment Plan) lets you invest a fixed amount in a mutual fund every month, automatically and with discipline. It is the single easiest way for ordinary people to build wealth, without needing to time the market or have a large lump sum." },
      { t: "h2", text: "How a SIP works" },
      { t: "p", text: "You decide an amount, say ₹5,000, and a date, say the 1st of every month. On that date, the amount is automatically debited from your bank account and used to buy units of your chosen fund at that day's NAV. You keep doing this month after month, year after year, and your investment quietly compounds." },
      { t: "h2", text: "Why SIPs work so well" },
      { t: "ul", items: [
        "Rupee-cost averaging: you automatically buy more units when prices are low and fewer when high.",
        "Discipline: investing happens automatically, removing emotion and procrastination.",
        "Compounding: small monthly amounts snowball into large corpuses over the years.",
        "Affordable: you can start with as little as ₹500 a month.",
        "No market timing needed: you invest steadily regardless of market highs and lows.",
      ] },
      { t: "h2", text: "The power of compounding: an example" },
      { t: "p", text: "A ₹5,000 monthly SIP earning 12% a year grows to roughly ₹50 lakh in 20 years, from a total investment of just ₹12 lakh. The other ₹38 lakh is pure compounding. Stretch it to 30 years and the same SIP can grow to around ₹1.76 crore. The longer you stay invested, the more dramatic the result." },
      { t: "h2", text: "Step-up SIP: a pro move" },
      { t: "p", text: "A step-up (or top-up) SIP increases your monthly investment by a fixed percentage each year, say 10%, in line with your rising income. This small habit can dramatically boost your final corpus without straining your budget, because the increase comes from salary growth you'd otherwise spend." },
      { t: "h2", text: "Common SIP mistakes" },
      { t: "ul", items: [
        "Stopping SIPs when markets fall, exactly when units are cheapest.",
        "Investing for too short a period to let compounding work.",
        "Picking funds based only on last year's returns.",
        "Not increasing the SIP amount as income grows.",
      ] },
    ],
  },
  {
    slug: "how-to-start-investing",
    category: "education",
    title: "How to Start Investing: A Step-by-Step Guide",
    emoji: "🚀",
    readTime: 10,
    excerpt: "From zero to your first SIP in five simple steps.",
    content: [
      { t: "lead", text: "Starting is the hardest part of investing. The good news is that you don't need a lot of money or any special expertise, just a clear path and the willingness to begin. Here is exactly how to go from never having invested to owning your first portfolio." },
      { t: "h2", text: "Before you invest: lay the foundation" },
      { t: "p", text: "Investing should never come before financial safety. Make sure you have a small emergency fund and adequate health insurance first. This ensures that an unexpected expense won't force you to sell your investments at the worst possible time." },
      { t: "h2", text: "Five steps to begin" },
      { t: "ul", items: [
        "Step 1: Secure the basics, an emergency fund and health insurance, before investing.",
        "Step 2: Define your goal and time horizon, such as building wealth over 15 years.",
        "Step 3: Complete your KYC and open a demat or investment account online.",
        "Step 4: Start a SIP in a low-cost index fund or a diversified equity fund.",
        "Step 5: Automate the SIP and review just once or twice a year; resist the urge to tinker.",
      ] },
      { t: "h2", text: "How much should you start with?" },
      { t: "p", text: "There is no minimum to be 'ready'. You can begin a SIP with as little as ₹500 a month. What matters far more than the amount is starting early and staying consistent. You can always increase the amount as your income grows and your confidence builds." },
      { t: "h2", text: "What should a beginner invest in?" },
      { t: "ul", items: [
        "A low-cost Nifty 50 index fund is an excellent, simple starting point.",
        "A diversified flexi-cap fund for slightly more active management.",
        "Avoid individual stocks, sector funds and 'hot tips' until you have more experience.",
        "Keep short-term money (needed within 2 to 3 years) in safe debt funds or FDs.",
      ] },
      { t: "h2", text: "The mindset that wins" },
      { t: "p", text: "You don't need to be rich to invest; you need to be consistent. Markets will rise and fall, but disciplined, long-term investors are the ones who build wealth. Start small, start now, and let time do the heavy lifting." },
    ],
  },
  {
    slug: "risk-vs-return",
    category: "education",
    title: "Risk vs Return: The Most Important Trade-off",
    emoji: "🎯",
    readTime: 9,
    excerpt: "Why higher returns always come with higher risk, and how to balance it.",
    content: [
      { t: "lead", text: "There is no free lunch in investing. Every potential return carries a matching level of risk, and understanding this trade-off is the heart of smart investing. Once you grasp it, you'll stop chasing 'guaranteed high returns' and start making sound decisions." },
      { t: "h2", text: "What 'risk' really means" },
      { t: "p", text: "In investing, risk is the chance that your actual return differs from what you expected, including the possibility of losing money. Higher potential returns always come bundled with higher risk. Anyone promising high returns with no risk is either mistaken or running a scam." },
      { t: "h2", text: "The risk-return ladder" },
      { t: "ul", items: [
        "Low risk, low return: savings accounts, FDs, government bonds.",
        "Medium risk, medium return: hybrid funds, large-cap equity, gold.",
        "High risk, high return: mid-cap and small-cap stocks, sector and thematic funds.",
      ] },
      { t: "h2", text: "Types of investment risk" },
      { t: "ul", items: [
        "Market risk: prices fall due to overall market movements.",
        "Inflation risk: 'safe' returns fail to beat inflation, eroding buying power.",
        "Liquidity risk: you can't sell an asset quickly without a loss.",
        "Concentration risk: too much money in a single stock or sector.",
      ] },
      { t: "h2", text: "Match risk to your time horizon" },
      { t: "p", text: "Time is the great equaliser. Money you need within 1 to 2 years should stay safe in debt funds or FDs, because a market dip could hurt right when you need it. Money you won't touch for 7 or more years can take on equity risk, because over long periods, market volatility tends to smooth out into solid growth." },
      { t: "h2", text: "Know your risk appetite" },
      { t: "p", text: "Your ideal risk level depends on your age, income stability, goals and temperament. A young investor with a stable job can afford more equity; someone near retirement should protect their capital. The best portfolio is one you can stick with calmly, even when markets are turbulent." },
    ],
  },
  {
    slug: "asset-allocation",
    category: "education",
    title: "Asset Allocation: The Strategy That Drives Returns",
    emoji: "🧩",
    readTime: 9,
    excerpt: "How splitting money across asset classes protects and grows wealth.",
    content: [
      { t: "lead", text: "Studies consistently show that how you split your money between asset classes, equity, debt and gold, matters far more to your long-term returns than which specific funds or stocks you pick. Asset allocation is the quiet strategy behind almost every successful portfolio." },
      { t: "h2", text: "What is asset allocation?" },
      { t: "p", text: "Asset allocation is the mix of different asset classes in your portfolio. Equity offers growth but is volatile; debt offers stability but lower returns; gold acts as a hedge during uncertainty. The right blend depends on your goals, time horizon and risk appetite, and it determines both how much you earn and how much you can stomach when markets swing." },
      { t: "h2", text: "The main asset classes" },
      { t: "ul", items: [
        "Equity: stocks and equity funds; high growth, high volatility, best for the long term.",
        "Debt: bonds, debt funds, FDs; stable and predictable, lower returns.",
        "Gold: a hedge against inflation and market stress; keep it modest.",
        "Cash: liquid funds and savings for emergencies and short-term needs.",
      ] },
      { t: "h2", text: "A simple rule of thumb" },
      { t: "p", text: "One classic guideline is: equity allocation ≈ 100 minus your age. A 30-year-old might hold around 70% equity and 30% debt, while a 60-year-old might flip that. It is only a starting point, adjust it for your own goals and comfort with risk." },
      { t: "h2", text: "Why rebalancing matters" },
      { t: "ul", items: [
        "Over time, your winners grow and skew your allocation away from the plan.",
        "Rebalancing once a year brings it back to target, automatically selling high and buying low.",
        "It keeps your risk level aligned with your goals instead of drifting with the market.",
      ] },
      { t: "h2", text: "Example of rebalancing" },
      { t: "p", text: "Say you target 70% equity and 30% debt. After a strong year, equity grows to 80% of your portfolio. Rebalancing means selling some equity and adding to debt to return to 70/30. This disciplined habit forces you to book profits and buy the cheaper asset, exactly what emotion usually prevents us from doing." },
    ],
  },

  /* ------------------ BANKING ------------------ */
  {
    slug: "best-savings-accounts",
    category: "banking",
    title: "How to Choose the Best Savings Account",
    emoji: "🏦",
    readTime: 9,
    excerpt: "Interest rates, fees and features that actually matter.",
    content: [
      { t: "lead", text: "Your savings account is the hub of your money, where your salary lands and your everyday spending flows from. Choosing the right one saves you on fees, earns you more interest and makes daily banking effortless. Here's how to pick wisely." },
      { t: "h2", text: "Why your choice of account matters" },
      { t: "p", text: "Most people open a savings account once and never review it. Yet the difference between a poor account and a good one can be thousands of rupees a year in extra interest and avoided fees. A few minutes of comparison pays off for years." },
      { t: "h2", text: "What to compare" },
      { t: "ul", items: [
        "Interest rate: some banks offer 6 to 7% versus the typical 3 to 4%.",
        "Minimum balance requirement and the penalty for breaching it.",
        "Free transactions, ATM withdrawals and the quality of digital and mobile banking.",
        "Sweep-in FD facility that auto-earns higher returns on idle cash.",
        "Branch and ATM network if you still need physical banking.",
        "Hidden charges for SMS alerts, debit cards and account maintenance.",
      ] },
      { t: "h2", text: "Types of savings accounts" },
      { t: "ul", items: [
        "Regular savings: standard account for everyday use.",
        "Zero-balance/salary accounts: no minimum balance, ideal for salaried employees.",
        "High-interest digital accounts: offered by new-age and small finance banks.",
        "Senior citizen accounts: often with higher rates and extra benefits.",
      ] },
      { t: "h2", text: "Smart tip: don't let cash sit idle" },
      { t: "p", text: "A savings account is for liquidity, not growth, its interest barely beats inflation. Keep only a buffer for your monthly needs and move the rest into a liquid fund, sweep-in FD or short-term FD where it can earn meaningfully more while staying accessible." },
      { t: "h2", text: "Stay safe" },
      { t: "ul", items: [
        "Deposits are insured up to ₹5 lakh per bank under DICGC.",
        "Never share your OTP, PIN or net-banking password with anyone.",
        "Enable transaction alerts to catch unauthorised activity instantly.",
      ] },
    ],
  },
  {
    slug: "credit-card-guides",
    category: "banking",
    title: "Credit Cards 101: Use Them Without Getting Burned",
    emoji: "💳",
    readTime: 10,
    excerpt: "Rewards, billing cycles and the golden rules of credit cards.",
    content: [
      { t: "lead", text: "Used well, a credit card is a free 45-day loan that pays you rewards. Used badly, it is the most expensive debt you can carry. The card itself is neutral; your habits decide whether it builds your wealth or drains it." },
      { t: "h2", text: "How a credit card actually works" },
      { t: "p", text: "When you swipe a card, the bank pays the merchant and lends you that money interest-free until your bill is due, typically up to 45 days. If you pay the full statement balance by the due date, you pay zero interest. If you don't, interest, often 36 to 48% a year, kicks in on the entire balance." },
      { t: "h2", text: "Understand the billing cycle" },
      { t: "p", text: "Your statement covers a fixed period (say, the 5th of one month to the 4th of the next). Purchases made right at the start of a cycle enjoy the longest interest-free period. The 'minimum due' is a trap, paying only that keeps you in expensive debt while interest piles up." },
      { t: "h2", text: "Golden rules" },
      { t: "ul", items: [
        "Always pay the full statement balance, never just the minimum due.",
        "Keep utilisation under 30% of your limit to protect your credit score.",
        "Pick a card whose rewards match your real spending, such as fuel, travel or shopping.",
        "Avoid cash withdrawals on a credit card; they attract interest from day one plus a fee.",
        "Set up auto-pay for the full amount so you never miss a due date.",
      ] },
      { t: "h2", text: "Making rewards work for you" },
      { t: "ul", items: [
        "Choose a card aligned to your biggest spending category.",
        "Use reward points and cashback, but only on purchases you'd make anyway.",
        "Watch the annual fee; ensure the benefits exceed what you pay.",
        "Don't overspend just to chase rewards, that defeats the purpose.",
      ] },
      { t: "h2", text: "Watch the interest trap" },
      { t: "p", text: "Credit card interest can run 36 to 48% per year, among the highest of any borrowing. Carrying a balance for even a few months can wipe out years of rewards and quietly trap you in a debt cycle. The single rule that keeps you safe: treat your card like a debit card and pay it in full every month." },
    ],
  },
  {
    slug: "personal-loan-guides",
    category: "banking",
    title: "Personal Loans: When They Make Sense (and When They Don't)",
    emoji: "📄",
    readTime: 9,
    excerpt: "Interest rates, hidden charges and smarter alternatives.",
    content: [
      { t: "lead", text: "A personal loan is unsecured and quick to get, but it is also one of the costlier loans you can take. It can be a sensible tool or an expensive mistake, depending entirely on why and how you use it." },
      { t: "h2", text: "What is a personal loan?" },
      { t: "p", text: "A personal loan is borrowing that requires no collateral, the bank lends based on your income and credit score. Because it is unsecured, interest rates are higher than secured loans like home or auto loans, typically ranging from 11 to 24% depending on your profile." },
      { t: "h2", text: "Before you borrow" },
      { t: "ul", items: [
        "Compare the APR (interest rate plus fees), not just the headline rate.",
        "Check processing fees, which can be 1 to 3% of the loan amount.",
        "Read the prepayment and foreclosure charges before signing.",
        "Borrow only what you need and pick the shortest tenure you can comfortably afford.",
        "A higher credit score (750+) gets you a meaningfully lower interest rate.",
      ] },
      { t: "h2", text: "Good reasons vs bad reasons" },
      { t: "ul", items: [
        "Reasonable: consolidating high-interest credit card debt into a lower-rate loan.",
        "Reasonable: a genuine medical or family emergency with no cheaper option.",
        "Questionable: funding a vacation, wedding splurge or gadget you could save up for.",
        "Avoid: borrowing to invest or to repay another loan you can't manage.",
      ] },
      { t: "h2", text: "Smarter alternatives to consider" },
      { t: "ul", items: [
        "An emergency fund, which costs nothing in interest.",
        "A loan against FD or other assets, usually at a lower rate.",
        "A top-up on an existing home loan, often the cheapest borrowing available.",
      ] },
      { t: "p", text: "If you must take a personal loan, treat repayment as a priority and clear it as fast as your budget allows." },
    ],
  },
  {
    slug: "home-loan-guides",
    category: "banking",
    title: "Home Loan Guide: Get the Best Deal",
    emoji: "🏠",
    readTime: 11,
    excerpt: "Fixed vs floating, tenure strategy and how to save lakhs in interest.",
    content: [
      { t: "lead", text: "A home loan is likely the biggest and longest debt you'll ever take. Because the amounts and timeframes are so large, small choices here can save, or cost, you several lakh rupees over the life of the loan. Here's how to get it right." },
      { t: "h2", text: "Fixed vs floating interest rate" },
      { t: "p", text: "A fixed rate stays constant for a set period, giving predictability. A floating rate moves with market rates and is usually cheaper over the long term in India. Most borrowers choose floating, especially since floating-rate home loans carry no prepayment penalty." },
      { t: "h2", text: "Key decisions that affect your cost" },
      { t: "ul", items: [
        "Tenure: a shorter tenure means a higher EMI but far less total interest paid.",
        "Down payment: a larger down payment lowers your loan and your interest burden.",
        "Interest rate: even a 0.25% difference adds up to lakhs over 20 years, so compare lenders.",
        "Processing fees and other charges, which vary between banks.",
        "Your credit score, which directly determines the rate you're offered.",
      ] },
      { t: "h2", text: "The power of prepayment" },
      { t: "p", text: "Early EMIs are mostly interest, so prepaying in the first years of the loan has the biggest impact. Even one extra EMI a year, or small lump-sum prepayments, can cut several years off your tenure and save lakhs in interest. Use our EMI Calculator to test different prepayment scenarios." },
      { t: "h2", text: "Tax benefits on home loans" },
      { t: "ul", items: [
        "Section 80C: deduction up to ₹1.5 lakh on principal repayment.",
        "Section 24(b): deduction up to ₹2 lakh on interest for a self-occupied home.",
        "These benefits apply under the old tax regime.",
      ] },
      { t: "h2", text: "Should you prepay or invest?" },
      { t: "p", text: "If your home loan rate is higher than what your investments reliably earn after tax, prepaying is the safer win. If you can earn more by investing (for example in long-term equity) and value liquidity, a balanced approach, partial prepayment plus investing, often works best." },
    ],
  },
  {
    slug: "banking-news",
    category: "banking",
    title: "Banking Trends to Watch in 2026",
    emoji: "📡",
    readTime: 8,
    excerpt: "UPI growth, digital banks and what's changing for customers.",
    content: [
      { t: "lead", text: "Banking in India is changing faster than ever, driven by technology, regulation and changing customer expectations. Here are the key trends shaping how Indians save, spend and borrow in 2026, and what they mean for you." },
      { t: "h2", text: "The UPI revolution continues" },
      { t: "p", text: "UPI has transformed payments in India and keeps growing. It is expanding beyond simple transfers into credit lines on UPI, offline payments, and international acceptance. For users, this means faster, cheaper and more convenient transactions than ever before." },
      { t: "h2", text: "Key trends to watch" },
      { t: "ul", items: [
        "Digital-first neobanks offering better rates and seamless, app-based experiences.",
        "AI-driven fraud detection and personalised money insights built into banking apps.",
        "The Account Aggregator framework giving you control over sharing your financial data securely.",
        "Rising adoption of credit-on-UPI, blending borrowing with everyday payments.",
        "Greater focus on financial inclusion reaching rural and underbanked users.",
      ] },
      { t: "h2", text: "What it means for you" },
      { t: "ul", items: [
        "More choice and better rates, so review your bank and accounts periodically.",
        "Greater convenience, but also a greater need for digital vigilance.",
        "More control over your own financial data through consent-based sharing.",
      ] },
      { t: "h2", text: "Stay safe in a digital world" },
      { t: "p", text: "As banking goes digital, scams evolve too. Never share OTPs, PINs or passwords with anyone, and be wary of unsolicited calls or links. Keep your apps updated, use strong authentication, and remember: convenience and security must always go together." },
    ],
  },

  /* ------------------ TAX (INDIA) ------------------ */
  {
    slug: "income-tax-guide",
    category: "tax",
    title: "Income Tax Guide (India): Old vs New Regime",
    emoji: "📋",
    readTime: 11,
    excerpt: "Understand slabs, deductions and which regime saves you more.",
    content: [
      { t: "lead", text: "India offers two income tax regimes, and choosing the right one can save you tens of thousands of rupees a year. The new regime has lower rates but almost no deductions; the old regime has higher rates but lets you claim many exemptions. Here's how to decide." },
      { t: "h2", text: "The two regimes explained" },
      { t: "p", text: "The new regime is now the default. It offers lower, simpler slab rates and a standard deduction, but you give up most exemptions like 80C, HRA and home loan interest. The old regime keeps the traditional higher rates but rewards you for tax-saving investments and eligible expenses." },
      { t: "h2", text: "Which regime suits you?" },
      { t: "ul", items: [
        "New regime: best if you have few deductions and prefer simplicity.",
        "Old regime: best if you maximise 80C, HRA, home loan interest and other deductions.",
        "Run both numbers every year, because the better choice can change as your finances change.",
      ] },
      { t: "h2", text: "Key deductions under the old regime" },
      { t: "ul", items: [
        "Section 80C: up to ₹1.5 lakh (ELSS, PPF, EPF, life insurance, etc.).",
        "Section 80CCD(1B): an extra ₹50,000 for NPS contributions.",
        "Section 80D: health insurance premiums for you and your parents.",
        "HRA: house rent allowance, if you live in rented accommodation.",
        "Section 24(b): up to ₹2 lakh on home loan interest.",
      ] },
      { t: "h2", text: "A simple rule of thumb" },
      { t: "p", text: "If your total deductions are large (typically above ₹3.75 to ₹4 lakh for higher incomes), the old regime usually wins. If you don't invest much in tax-saving instruments or rent a home, the new regime is often better. The only way to be sure is to calculate both." },
      { t: "h2", text: "Don't forget" },
      { t: "ul", items: [
        "A 4% health and education cess applies on the tax in both regimes.",
        "A rebate under Section 87A makes income up to a threshold effectively tax-free.",
        "Salaried individuals can switch regimes each year; business income has stricter rules.",
      ] },
      { t: "p", text: "Use our Income Tax Calculator to compare both regimes for your salary in seconds." },
    ],
  },
  {
    slug: "section-80c-explained",
    category: "tax",
    title: "Section 80C Explained: Save Up to ₹46,800 in Tax",
    emoji: "💸",
    readTime: 10,
    excerpt: "Every 80C option compared by returns, lock-in and risk.",
    content: [
      { t: "lead", text: "Section 80C is the most popular tax-saving tool in India. It lets you reduce your taxable income by up to ₹1.5 lakh a year, potentially saving ₹46,800 in tax for those in the 30% bracket. The trick is choosing options that save tax and grow your money well." },
      { t: "h2", text: "How Section 80C works" },
      { t: "p", text: "Under the old tax regime, you can deduct up to ₹1.5 lakh of eligible investments and expenses from your taxable income each financial year. Your tax saving equals ₹1.5 lakh multiplied by your tax slab, so ₹46,800 at 30%, ₹31,200 at 20%, and so on. Note: 80C is not available under the new regime." },
      { t: "h2", text: "Popular 80C investments" },
      { t: "ul", items: [
        "ELSS funds: equity returns with the shortest lock-in of just 3 years.",
        "PPF: around 7.1% tax-free returns with a 15-year lock-in; fully safe.",
        "EPF: automatically deducted for salaried employees.",
        "Life insurance premiums (preferably term insurance, not endowment plans).",
        "5-year tax-saver fixed deposits for the risk-averse.",
        "Sukanya Samriddhi Yojana for a girl child; NSC; and home loan principal repayment.",
        "Children's school tuition fees also qualify.",
      ] },
      { t: "h2", text: "Comparing the options" },
      { t: "ul", items: [
        "Highest growth potential: ELSS (equity, market-linked).",
        "Shortest lock-in: ELSS at 3 years.",
        "Safest and tax-free: PPF.",
        "Lowest effort: EPF, since it's automatic.",
      ] },
      { t: "h2", text: "Smart strategy" },
      { t: "p", text: "If you want growth and the shortest lock-in, ELSS is the best 80C pick. For guaranteed safety, PPF wins. Many investors use a mix: ELSS for growth and PPF for stability. Avoid buying high-cost insurance policies purely to save tax, keep insurance and investment separate." },
    ],
  },
  {
    slug: "tax-saving-investments",
    category: "tax",
    title: "Best Tax-Saving Investments Beyond 80C",
    emoji: "🧮",
    readTime: 9,
    excerpt: "Extra deductions most taxpayers forget to claim.",
    content: [
      { t: "lead", text: "Section 80C isn't the only way to save tax. Most taxpayers stop at the ₹1.5 lakh 80C limit and leave money on the table. These additional deductions, all available under the old regime, can cut your tax bill significantly further." },
      { t: "h2", text: "Deductions beyond the ₹1.5 lakh limit" },
      { t: "ul", items: [
        "Section 80CCD(1B): an extra ₹50,000 deduction for NPS contributions, over and above 80C.",
        "Section 80D: health insurance, up to ₹25,000 for self and family, plus ₹25,000 to ₹50,000 for parents.",
        "Section 80E: the full interest paid on an education loan, with no upper limit, for up to 8 years.",
        "Section 24(b): up to ₹2 lakh on home loan interest for a self-occupied property.",
        "Section 80TTA/80TTB: deduction on savings interest (and more for senior citizens).",
        "Section 80G: donations to eligible charities and funds.",
      ] },
      { t: "h2", text: "How much can you really save?" },
      { t: "p", text: "Stacking 80C (₹1.5 lakh) + 80CCD(1B) (₹50,000) + 80D (up to ₹75,000) + home loan interest (₹2 lakh) can push your total deductions well past ₹4 lakh. For someone in the 30% bracket, that translates to over ₹1.2 lakh in tax savings, all of it legal and straightforward." },
      { t: "h2", text: "Important note" },
      { t: "p", text: "Almost all of these deductions apply only under the old tax regime. If you choose the new regime for its lower rates, you forgo most of them. Always compare both regimes before deciding which one minimises your total tax." },
    ],
  },
  {
    slug: "itr-filing-guide",
    category: "tax",
    title: "ITR Filing Guide: File Your Returns Without Stress",
    emoji: "🗂️",
    readTime: 9,
    excerpt: "Documents, steps and common mistakes to avoid.",
    content: [
      { t: "lead", text: "Filing your Income Tax Return (ITR) sounds daunting, but for most salaried individuals it is a simple, mostly online process. Filing on time keeps you compliant, helps you claim refunds and is often needed for loans and visas. Here's a clean, step-by-step approach." },
      { t: "h2", text: "Who needs to file?" },
      { t: "p", text: "You generally must file if your income exceeds the basic exemption limit. Even if it doesn't, filing is wise, it lets you claim refunds on excess TDS, builds a financial record, and is frequently required for loan and visa applications." },
      { t: "h2", text: "Documents to keep ready" },
      { t: "ul", items: [
        "Form 16 from your employer (summarises salary and TDS).",
        "Form 26AS and the Annual Information Statement (AIS), which show taxes paid and reported income.",
        "Bank interest certificates and capital gains statements from your broker or fund house.",
        "Proofs for the deductions you're claiming, such as 80C and 80D.",
        "Your PAN, Aadhaar and bank account details.",
      ] },
      { t: "h2", text: "Steps to file" },
      { t: "ul", items: [
        "Step 1: Log in to the income tax e-filing portal.",
        "Step 2: Pick the correct ITR form for your income type (ITR-1 for most salaried people).",
        "Step 3: Verify the pre-filled data against Form 26AS and AIS.",
        "Step 4: Enter deductions, confirm tax payable or refund, and submit.",
        "Step 5: E-verify within 30 days, your filing is not complete without verification.",
      ] },
      { t: "h2", text: "Common mistakes to avoid" },
      { t: "ul", items: [
        "Missing the deadline, which attracts penalties and interest.",
        "Forgetting to report interest income or capital gains.",
        "Choosing the wrong ITR form for your income type.",
        "Not e-verifying after submission, leaving the return invalid.",
      ] },
    ],
  },
  {
    slug: "capital-gain-tax",
    category: "tax",
    title: "Capital Gains Tax in India: A Clear Breakdown",
    emoji: "📑",
    readTime: 9,
    excerpt: "Short-term vs long-term tax on stocks, funds and property.",
    content: [
      { t: "lead", text: "When you sell an asset, such as shares, mutual funds or property, for more than you paid, the profit is a 'capital gain' and is taxable. How much tax you pay depends on the type of asset and how long you held it. Here's a clear breakdown." },
      { t: "h2", text: "Short-term vs long-term" },
      { t: "p", text: "The key factor is your holding period. Hold an asset for longer than a defined threshold and it qualifies as a long-term capital gain (LTCG), which is usually taxed more favourably. Sell sooner and it is a short-term capital gain (STCG), taxed at a higher rate." },
      { t: "h2", text: "Equity and equity mutual funds" },
      { t: "ul", items: [
        "Short-term (held under 1 year): gains taxed at 20%.",
        "Long-term (held over 1 year): 12.5% on gains above ₹1.25 lakh per financial year.",
      ] },
      { t: "h2", text: "Property and debt funds" },
      { t: "ul", items: [
        "Property held over 2 years: long-term gains taxed at 12.5% (subject to applicable indexation rules).",
        "Property sold within 2 years: gains added to income and taxed at your slab.",
        "Debt funds: gains taxed at your income slab regardless of holding period.",
      ] },
      { t: "h2", text: "Smart, legal ways to reduce capital gains tax" },
      { t: "ul", items: [
        "Tax harvesting: book up to ₹1.25 lakh of long-term equity gains each year tax-free, then reinvest.",
        "Offset gains with capital losses from other investments (loss set-off).",
        "Hold equity investments beyond a year to qualify for the lower LTCG rate.",
        "Reinvest property gains in another house or specified bonds to claim exemptions.",
      ] },
      { t: "p", text: "Tip: harvesting ₹1.25 lakh of tax-free long-term equity gains every year is one of the simplest ways to reduce your eventual tax bill." },
    ],
  },

  /* ------------------ TRENDING ------------------ */
  {
    slug: "best-sip-funds-2026",
    category: "trending",
    title: "How to Pick the Best SIP Funds in 2026",
    emoji: "🔥",
    readTime: 9,
    excerpt: "A framework for choosing winning funds, not a hot-tip list.",
    content: [
      { t: "lead", text: "Everyone wants to know the single 'best' SIP fund, but no one can guarantee it in advance. What you can control is the selection process. A solid framework dramatically improves your odds of picking a fund that compounds your wealth for years." },
      { t: "h2", text: "Why a framework beats hot tips" },
      { t: "p", text: "Last year's chart-topping fund is rarely next year's winner. Chasing recent performance often means buying high and being disappointed. A disciplined framework, focused on consistency and cost, keeps you invested in quality funds through market cycles." },
      { t: "h2", text: "What to look for" },
      { t: "ul", items: [
        "Consistent long-term performance across multiple market cycles, not just the last year.",
        "A low expense ratio, always choose direct plans over regular ones.",
        "Experienced fund management and a clear, stable investment mandate.",
        "A category that fits your goal, such as index, flexi-cap or large-cap.",
        "Reasonable assets under management and low portfolio churn.",
      ] },
      { t: "h2", text: "Match the fund type to your goal" },
      { t: "ul", items: [
        "Beginner or low-effort: a low-cost Nifty 50 index fund.",
        "Long-term growth: a diversified flexi-cap fund.",
        "Lower volatility: a large-cap or balanced advantage fund.",
        "Higher risk appetite (long horizon): a small allocation to mid or small-cap funds.",
      ] },
      { t: "h2", text: "A simple starter portfolio" },
      { t: "p", text: "Many investors do well with a low-cost Nifty 50 index fund as the core, plus a flexi-cap fund for active growth. That's it. Keep it simple, automate the SIP, increase it as your income grows, and resist the urge to constantly switch funds." },
    ],
  },
  {
    slug: "best-investment-options-india",
    category: "trending",
    title: "Best Investment Options in India for 2026",
    emoji: "🌟",
    readTime: 10,
    excerpt: "A risk-wise menu from safe FDs to high-growth equity.",
    content: [
      { t: "lead", text: "There is no single 'best' investment, only the best investment for your goal, timeline and risk appetite. The right approach is to build a menu across the risk spectrum and match each option to a specific purpose. Here's a balanced overview for 2026." },
      { t: "h2", text: "Safe options (capital protection)" },
      { t: "ul", items: [
        "PPF: tax-free, government-backed, ideal for long-term safe goals.",
        "Fixed deposits: guaranteed returns with flexible tenures.",
        "Government bonds and debt funds: stable income with low risk.",
        "Liquid funds: for your emergency fund and short-term parking.",
      ] },
      { t: "h2", text: "Moderate options (balanced growth)" },
      { t: "ul", items: [
        "Hybrid and balanced advantage funds: a managed mix of equity and debt.",
        "Large-cap equity funds: growth with relatively lower volatility.",
        "Gold via Sovereign Gold Bonds: a hedge that also pays interest.",
      ] },
      { t: "h2", text: "Growth options (long-term wealth)" },
      { t: "ul", items: [
        "Index funds: low-cost exposure to the broad market.",
        "Flexi-cap funds: diversified across company sizes.",
        "Mid and small-cap funds: higher potential returns with higher risk, for long horizons only.",
      ] },
      { t: "h2", text: "For retirement" },
      { t: "p", text: "The NPS offers tax-efficient, disciplined long-term saving with an extra ₹50,000 deduction, making it a strong addition to your retirement stack alongside equity SIPs." },
      { t: "h2", text: "The golden rule" },
      { t: "p", text: "Diversify across asset classes and match each investment to a specific goal and time horizon. Never put money you'll need within 2 to 3 years into equity, and never let long-term money sit idle in a savings account losing to inflation." },
    ],
  },
  {
    slug: "passive-income",
    category: "trending",
    title: "How to Earn Passive Income in India",
    emoji: "💵",
    readTime: 9,
    excerpt: "Realistic passive income ideas, and the myths to ignore.",
    content: [
      { t: "lead", text: "Passive income is money that flows in with little ongoing effort. The honest truth is that most 'passive' income requires either upfront capital or upfront work to set up. Here's what genuinely works in India, and what to avoid." },
      { t: "h2", text: "Capital-based passive income" },
      { t: "p", text: "These options require money upfront, which then works for you. The more you invest, the more income they generate." },
      { t: "ul", items: [
        "Dividend-paying stocks and dividend mutual funds.",
        "Interest from FDs, bonds and debt funds.",
        "Rental income from property.",
        "Sovereign Gold Bond interest plus gold price appreciation.",
        "A systematic withdrawal plan (SWP) from a mutual fund corpus.",
      ] },
      { t: "h2", text: "Effort-based (semi-passive) income" },
      { t: "p", text: "These require significant work upfront, but can pay you for years afterward with minimal maintenance." },
      { t: "ul", items: [
        "Digital products, online courses or e-books.",
        "A blog or YouTube channel that earns through ads over time.",
        "Affiliate income from content you already create.",
        "Stock photography, templates or apps.",
      ] },
      { t: "h2", text: "How to start realistically" },
      { t: "p", text: "Begin by building investment capital through your job and SIPs; over time, dividends, interest and SWPs become meaningful passive streams. In parallel, you can create a digital asset using your skills. Be patient, real passive income compounds slowly." },
      { t: "h2", text: "Myths to ignore" },
      { t: "p", text: "Be very wary of any scheme promising 'guaranteed high returns' with no risk or effort, these are the most common traps and often outright scams. Genuine passive income is built, not handed out." },
    ],
  },
  {
    slug: "side-hustle-ideas",
    category: "trending",
    title: "15 Side Hustle Ideas to Boost Your Income",
    emoji: "💼",
    readTime: 9,
    excerpt: "Flexible ways to earn extra, online and offline.",
    content: [
      { t: "lead", text: "A side hustle is one of the fastest ways to accelerate your financial goals. Even an extra ₹10,000 a month, consistently invested, can grow into serious wealth over time. The best part: many side hustles can start with little or no money." },
      { t: "h2", text: "Why a side hustle is powerful" },
      { t: "p", text: "Cutting expenses has a limit, but increasing your income does not. A side hustle adds a second stream of money you can channel straight into investments, dramatically shortening the time to reach your goals. ₹10,000 a month invested at 12% becomes over ₹23 lakh in 10 years." },
      { t: "h2", text: "Online side hustles" },
      { t: "ul", items: [
        "Freelancing: writing, graphic design, coding or video editing.",
        "Online tutoring or teaching a skill you already have.",
        "Content creation on YouTube, blogging or social media.",
        "Selling digital products or print-on-demand merchandise.",
        "Virtual assistance, data entry or transcription.",
        "Managing social media for small businesses.",
      ] },
      { t: "h2", text: "Offline side hustles" },
      { t: "ul", items: [
        "Tuition classes or weekend coaching.",
        "Home baking, handmade crafts or reselling products.",
        "Event or wedding photography.",
        "Renting out a spare room, parking space or equipment.",
        "Driving, delivery or local gig work on weekends.",
      ] },
      { t: "h2", text: "How to choose and succeed" },
      { t: "p", text: "Pick something aligned with your existing skills and interests so you can sustain it. Start small alongside your main job, deliver quality, and reinvest the earnings into your SIPs rather than spending them. Consistency turns a small side hustle into a wealth machine." },
    ],
  },
  {
    slug: "financial-freedom-fire",
    category: "trending",
    title: "Financial Freedom (FIRE): Retire Early, Live Free",
    emoji: "🔥",
    readTime: 10,
    excerpt: "The FIRE movement explained, and how to calculate your number.",
    content: [
      { t: "lead", text: "FIRE stands for Financial Independence, Retire Early. It's a movement built on saving aggressively and investing wisely, so that one day your investments generate enough to cover your living costs, freeing you from the need to work for money." },
      { t: "h2", text: "What financial independence really means" },
      { t: "p", text: "Financial independence is the point where your investment income can sustain your lifestyle indefinitely. You may still choose to work, but you no longer have to. 'Retire early' is optional; the real prize is the freedom and choice that financial independence brings." },
      { t: "h2", text: "Your FIRE number" },
      { t: "p", text: "A widely used rule says you're financially independent when your investments equal about 25 times your annual expenses. This is based on the 4% safe withdrawal rule: withdrawing 4% of your corpus a year can, historically, sustain your lifestyle indefinitely. If you spend ₹6 lakh a year, your FIRE number is roughly ₹1.5 crore." },
      { t: "h2", text: "How to get there" },
      { t: "ul", items: [
        "Raise your savings rate aggressively, FIRE seekers aim for 40 to 60% of income.",
        "Invest the surplus consistently in low-cost equity funds.",
        "Avoid lifestyle inflation as your income grows.",
        "Build multiple income streams to accelerate the journey.",
        "Track your net worth and progress toward your FIRE number.",
      ] },
      { t: "h2", text: "Variations of FIRE" },
      { t: "ul", items: [
        "Lean FIRE: a minimalist lifestyle requiring a smaller corpus.",
        "Fat FIRE: a more comfortable lifestyle requiring a larger corpus.",
        "Coast FIRE: invest enough early, then let it grow while you work to cover only current expenses.",
      ] },
      { t: "p", text: "FIRE isn't about deprivation; it's about intentional spending and prioritising freedom over consumption." },
    ],
  },
  {
    slug: "ai-and-finance",
    category: "trending",
    title: "AI & Finance: How Technology Is Changing Your Money",
    emoji: "🤖",
    readTime: 8,
    excerpt: "Robo-advisors, fraud detection and AI-powered planning.",
    content: [
      { t: "lead", text: "Artificial intelligence is quietly reshaping personal finance, from how you invest and budget to how your bank keeps you safe. Understanding these changes helps you take advantage of the benefits while staying in control of your money." },
      { t: "h2", text: "How AI is transforming finance" },
      { t: "p", text: "AI processes vast amounts of financial data to spot patterns humans would miss. Banks, investment apps and fintech companies use it to personalise services, automate decisions and detect risks in real time, often without you even noticing." },
      { t: "h2", text: "Where AI helps you" },
      { t: "ul", items: [
        "Robo-advisors build and automatically rebalance diversified portfolios at low cost.",
        "AI fraud detection flags suspicious transactions in real time to protect your money.",
        "Smart apps categorise your spending and suggest savings automatically.",
        "Personalised insights and reminders help you stick to your budget and goals.",
        "Chatbots and AI assistants answer money questions instantly.",
      ] },
      { t: "h2", text: "The risks to be aware of" },
      { t: "ul", items: [
        "AI tools can be wrong or biased; treat their suggestions as input, not gospel.",
        "Scammers also use AI, for deepfakes, voice cloning and convincing phishing.",
        "Over-reliance can mean you stop understanding your own finances.",
        "Data privacy: know what information you're sharing and with whom.",
      ] },
      { t: "h2", text: "Stay in control" },
      { t: "p", text: "AI is a powerful assistant, not a replacement for your own judgement. Use its insights to save time and make better decisions, but always understand where your money is going and why. The best results come from combining smart technology with your own financial literacy." },
    ],
  },

  /* ------------------ BLOG ------------------ */
  {
    slug: "5000-monthly-sip-corpus",
    category: "blog",
    title: "How Much Corpus Can a ₹5,000 Monthly SIP Build?",
    emoji: "📰",
    image: blogSip,
    readTime: 8,
    excerpt: "A ₹5,000 SIP looks small, see what it becomes over 10, 20 and 30 years.",
    content: [
      { t: "lead", text: "A ₹5,000 monthly SIP feels modest, barely the cost of a few dinners out. Yet compounding has a way of turning this small, consistent habit into a life-changing corpus. Let's run the actual numbers at a 12% annual return and see the magic for ourselves." },
      { t: "h2", text: "The numbers over time" },
      { t: "p", text: "Here is what a steady ₹5,000 monthly SIP can grow to at roughly 12% annual returns, the kind of long-term average a diversified equity fund has historically delivered." },
      { t: "ul", items: [
        "After 10 years: about ₹11.6 lakh (you invested ₹6 lakh).",
        "After 20 years: about ₹50 lakh (you invested ₹12 lakh).",
        "After 30 years: about ₹1.76 crore (you invested just ₹18 lakh).",
      ] },
      { t: "h2", text: "The lesson hidden in the numbers" },
      { t: "p", text: "Notice how the gap between what you invest and what you receive explodes over time. In 10 years your money roughly doubles; in 30 years it grows nearly tenfold. That widening gap is compounding, your returns start earning their own returns. The single biggest driver is not the amount, but the time you stay invested." },
      { t: "h2", text: "How to make it even bigger" },
      { t: "ul", items: [
        "Start as early as possible, every extra year of compounding matters enormously.",
        "Use a step-up SIP, increasing the amount 10% a year can dramatically boost the final corpus.",
        "Never stop during market falls, those are when your SIP buys the most units.",
        "Stay invested for the long haul instead of withdrawing early.",
      ] },
      { t: "h2", text: "A quick reality check" },
      { t: "p", text: "Returns are not guaranteed and markets will be volatile year to year. But over long periods, disciplined equity SIP investors have been well rewarded. The takeaway is simple: small amounts, invested consistently for a long time, beat large amounts invested late." },
      { t: "p", text: "Want to see your own numbers? Try our SIP Calculator and adjust the amount, return rate and tenure." },
    ],
  },
  {
    slug: "how-much-invest-at-30",
    category: "blog",
    title: "How Much Should You Invest at Age 30?",
    emoji: "📰",
    image: blogInvest30,
    readTime: 8,
    excerpt: "A practical investment benchmark for your 30s.",
    content: [
      { t: "lead", text: "Your 30s are the golden decade for building wealth. Your income is usually rising, your expenses may not have peaked yet, and crucially, you still have several decades for compounding to work its magic. The habits you build now shape your entire financial future." },
      { t: "h2", text: "A simple benchmark" },
      { t: "p", text: "As a rule of thumb, aim to invest at least 20 to 30% of your income. A widely cited milestone is to have roughly 1 times your annual salary invested by age 30, around 3 times by 40, and 6 times by 50. Don't panic if you're behind; what matters most is starting now and increasing steadily." },
      { t: "h2", text: "Where to put your money" },
      { t: "ul", items: [
        "Equity SIPs for long-term goals like retirement and wealth creation.",
        "PPF and NPS for the safe, tax-efficient core of your portfolio.",
        "An emergency fund and adequate term plus health insurance, before investing.",
        "A small allocation to gold (via SGBs) for diversification.",
      ] },
      { t: "h2", text: "Common mistakes in your 30s" },
      { t: "ul", items: [
        "Lifestyle inflation, spending every raise instead of investing part of it.",
        "Taking on too many EMIs for cars and gadgets.",
        "Delaying investing because 'there's still time', the most expensive mistake.",
        "Buying expensive insurance-cum-investment plans instead of term insurance plus SIPs.",
      ] },
      { t: "h2", text: "The single most important habit" },
      { t: "p", text: "Increase your SIP every year as your income grows. A step-up SIP, raising your monthly investment by even 10% annually, can nearly double your final corpus compared to a flat SIP. Automate it, stay consistent, and let your 30s do the heavy lifting for your future." },
    ],
  },
  {
    slug: "fd-vs-mutual-fund",
    category: "blog",
    title: "FD vs Mutual Fund: Which One Is Better?",
    emoji: "📰",
    image: blogFdMf,
    readTime: 8,
    excerpt: "Safety vs growth, a clear comparison for Indian savers.",
    content: [
      { t: "lead", text: "It's one of the most common money questions in India: should I put my money in a fixed deposit or a mutual fund? FDs offer guaranteed, predictable returns, while mutual funds offer higher growth potential with some risk. The right answer depends entirely on your goal and timeline." },
      { t: "h2", text: "Fixed deposits: the safe choice" },
      { t: "p", text: "An FD gives you a fixed, guaranteed return for a set period. Your capital is safe (insured up to ₹5 lakh per bank), and you know exactly what you'll get. The trade-offs: returns are modest (around 6 to 7.5%), and the interest is fully taxable at your income slab, which hurts higher earners." },
      { t: "h2", text: "Mutual funds: the growth choice" },
      { t: "p", text: "Mutual funds, especially equity funds, invest in markets and aim for higher long-term returns (historically around 10 to 12%). They aren't guaranteed and will fluctuate year to year, but over long periods they have outpaced FDs comfortably and more tax-efficiently." },
      { t: "h2", text: "Head to head" },
      { t: "ul", items: [
        "Returns: FD around 6 to 7% fixed; equity funds around 10 to 12% long-term (not guaranteed).",
        "Risk: FD nearly zero; funds carry market risk that smooths out over time.",
        "Tax: FD interest taxed at your slab; equity LTCG is taxed more favourably.",
        "Liquidity: both are accessible, though FDs may charge a small breakage penalty.",
      ] },
      { t: "h2", text: "The verdict" },
      { t: "p", text: "It's not really either-or. Use FDs (and debt funds) for short-term goals and money you can't afford to risk. Use equity mutual funds for long-term goals (7+ years), where time rides out the volatility and compounding works in your favour. Most people need both, FDs for safety and stability, mutual funds for growth." },
    ],
  },
  {
    slug: "best-tax-saving-investments-blog",
    category: "blog",
    title: "Best Tax Saving Investments: A Quick Comparison",
    emoji: "📰",
    image: blogTax,
    readTime: 8,
    excerpt: "ELSS, PPF, NPS and more, ranked for different needs.",
    content: [
      { t: "lead", text: "Saving tax should never mean locking your money into a poor investment. The best tax-saving options do double duty: they reduce your tax bill and grow your wealth. Here's how the top choices stack up, so you can pick what fits your goals." },
      { t: "h2", text: "Ranked by purpose" },
      { t: "ul", items: [
        "Best for growth with the shortest lock-in: ELSS funds (3 years, equity returns).",
        "Best for guaranteed safety: PPF (15 years, fully tax-free).",
        "Best for an extra deduction: NPS (an additional ₹50,000 under 80CCD(1B)).",
        "Best for the risk-averse: 5-year tax-saver fixed deposit.",
        "Automatic for the salaried: EPF, already deducted from your salary.",
      ] },
      { t: "h2", text: "Quick comparison" },
      { t: "ul", items: [
        "ELSS: highest growth potential, market-linked, 3-year lock-in.",
        "PPF: ~7.1% tax-free, zero risk, 15-year lock-in.",
        "NPS: market-linked, locked till 60, unique extra ₹50k deduction.",
        "Tax-saver FD: fixed return, fully safe, interest is taxable.",
      ] },
      { t: "h2", text: "A smart strategy" },
      { t: "p", text: "Many investors do well with a simple mix: ELSS for growth, PPF for safety, and NPS for the extra ₹50,000 deduction. This balances risk and return while maximising your deductions. Remember, these benefits apply under the old tax regime, so compare both regimes first." },
      { t: "p", text: "Read our full Section 80C guide for a detailed breakdown of every option." },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
export const getArticlesByCategory = (key) =>
  ARTICLES.filter((a) => a.category === key);
